import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    IconButton, CircularProgress, TextField, Button, Box, Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Select, MenuItem, FormControl
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ReportStore from '../DataRepository/Reports';
import { reportsModel } from '../Axios/AxiosConfig';
import Accordion from '@mui/material/Accordion';
import { CheckCircle, ArrowUpward, Edit } from "@mui/icons-material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AccordionActions from '@mui/material/AccordionActions';
import { Circle, AlertTriangle } from 'lucide-react';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';

function ViewReport() {
    const { Reports, isReportLoading, getReports } = ReportStore();
    const { UID, id, key } = useParams();
    const navigate = useNavigate();
    const selectedReport = useMemo(() => Reports?.find((e) => e?.id === id), [Reports, id]);
    const [isExtracting, setExtractingloading] = useState(false)
    const [summarizedText, setsummarizedText] = useState('');
    const [isSummarizeloading, setSummarizeloading] = useState(false);
    const summarizedTextRef = useRef(null);
    const extractedDataRef = useRef(null);


    const [editMode, setEditMode] = useState(false);

    const [editableReports, setEditableReports] = useState(() =>
        selectedReport?.Reports ? [...selectedReport.Reports] : []
    );

    const [editableReport, setEditableReport] = useState(() => {
        const ai = selectedReport?.Reports?.[key]?.ai_analysis;
        try {
            return ai && ai !== "AI analysis failed" ? JSON.parse(ai) : null;
        } catch {
            return null;
        }
    });



    const handleSingleParamChange = (type, paramIndex, field, value) => {
        setEditableReports(prev => {
            if (!prev) return prev;

            const updated = { ...prev };
            const targetList = [...(updated?.[type] || [])];
            targetList[paramIndex] = { ...targetList[paramIndex], [field]: value };
            updated[type] = targetList;

            return updated;
        });
    };

    const handleParamChange = (type, index, field, value) => {
        setEditableReport(prev => {
            if (!prev) return prev;

            const updated = { ...prev };
            const targetList = [...(updated[type] || [])];
            const updatedParam = { ...targetList[index], [field]: value };

            // If status is being changed, check if parameter needs to move between arrays
            if (field === 'status') {
                const isNormalStatus = value === 'normal';
                const currentlyInNormal = type === 'normal_parameters';

                // If status changed to normal but currently in abnormal, move to normal
                if (isNormalStatus && !currentlyInNormal) {
                    // Remove from abnormal_parameters
                    const abnormalList = [...(updated.abnormal_parameters || [])];
                    abnormalList.splice(index, 1);
                    updated.abnormal_parameters = abnormalList;

                    updated.normal_parameters = [...(updated.normal_parameters || []), updatedParam];
                    return updated;
                }


                if (!isNormalStatus && currentlyInNormal) {
                    const normalList = [...(updated.normal_parameters || [])];
                    normalList.splice(index, 1);
                    updated.normal_parameters = normalList;


                    updated.abnormal_parameters = [...(updated.abnormal_parameters || []), updatedParam];
                    return updated;
                }
            }

            // Normal update without moving
            targetList[index] = updatedParam;
            updated[type] = targetList;
            return updated;
        });
    };

    const handleSave = async () => {
        try {
            const updatedAI = JSON.stringify(editableReport);

            // Clone full array, update just one report
            const updatedReports = [...editableReports];
            updatedReports[key] = {
                ...updatedReports[key],
                ai_analysis: updatedAI,
            };

            const res = await reportsModel.post('/editAnalyzedReport', {
                id, // Patient/User ID
                editableReports: updatedReports, // Entire updated array
            });

            if (res.status === 200) {
                setEditableReports(updatedReports); // Update local state
                setEditMode(false);
            }
        } catch (error) {
            console.error("Error uploading report and extracting data:", error);
        }
    };


    const toggleEdit = () => {
        setEditMode(true);
    };


    useEffect(() => {
        const fetchReports = async () => {
            await getReports(UID);
        };

        fetchReports();
    }, [UID, isExtracting, editMode]);

    const getStatusTag = (status) => {
        switch ((status || '').toLowerCase()) {
            case 'low':
                return <span className="px-2 py-0.5 text-xs rounded bg-yellow-100 text-yellow-800 border border-yellow-300">Low</span>;
            case 'high':
                return <span className="px-2 py-0.5 text-xs rounded bg-red-100 text-red-800 border border-red-300">High</span>;
            case 'normal':
                return <span className="px-2 py-0.5 text-xs rounded bg-green-100 text-green-800 border border-green-300">Normal</span>;
            case 'borderline':
                return <span className="px-2 py-0.5 text-xs rounded bg-orange-100 text-orange-800 border border-orange-300">Borderline</span>;
            case 'borderline_low':
                return <span className="px-2 py-0.5 text-xs rounded bg-yellow-200 text-yellow-900 border border-yellow-400">Borderline Low</span>;
            case 'borderline_high':
                return <span className="px-2 py-0.5 text-xs rounded bg-orange-200 text-orange-900 border border-orange-400">Borderline High</span>;
            case 'critical':
                return <span className="px-2 py-0.5 text-xs rounded bg-purple-100 text-purple-800 border border-purple-300">Critical</span>;
            case 'missed':
                return <span className="px-2 py-0.5 text-xs rounded bg-gray-200 text-gray-600 border border-gray-300">Missed</span>;
            default:
                return <span className="px-2 py-0.5 text-xs rounded bg-gray-100 text-gray-700 border border-gray-200">Unknown</span>;
        }
    };


    const getIcon = (status) => {
        switch ((status || '').toLowerCase()) {
            case 'low':
            case 'high':
            case 'borderline_low':
            case 'borderline_high':
            case 'borderline':
            case 'critical':
                return <AlertTriangle className="text-red-500 w-5 h-5 mr-2" />;
            case 'normal':
                return <CheckCircle className="text-green-500 w-5 h-5 mr-2" />;
        }
    };

    const uploadReportandExtractData = async () => {
        try {
            setExtractingloading(true);

            const extractedresponse = await reportsModel.post('/reanalyzeReport',
                {
                    "id": id
                }
            );

            if (extractedresponse?.status === 200 && id) {
                // If ai_analysis is null, empty, or 'null', set edit mode
                const ai = selectedReport?.Reports?.[key]?.ai_analysis;
                if (!ai || ai === 'null' || ai === '') {
                    setEditMode(true);
                }
                setExtractingloading(false);
            }
        }
        catch (error) {
            console.error("Error uploading report and extracting data:", error);
            setExtractingloading(false);
        }
    }

    useEffect(() => {
        if (selectedReport?.extractedData && extractedDataRef.current !== selectedReport.extractedData) {
            extractedDataRef.current = selectedReport.extractedData;
            setExtractedData(selectedReport.extractedData);
        }
    }, [selectedReport]);

    const [extractedData, setExtractedData] = useState('');

    const selectedReportUrl = selectedReport?.Reports?.[key]?.Location || '';

    const extractFilenameFromS3Url = (url) => {
        try {
            const pathname = new URL(url).pathname;
            const parts = pathname.split('/');
            return parts[parts.length - 1];
        } catch (error) {
            console.error("Invalid URL:", error);
            return 'file.pdf';
        }
    };


    const handleDownload = async (url) => {
        try {
            const response = await fetch(url, { mode: 'cors' });
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);

            const filename = extractFilenameFromS3Url(url);

            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
        } catch (error) {
            console.error("Download failed:", error);
        }
    };

    useEffect(() => {
        const observer = new ResizeObserver(() => {
            console.log("Resize detected");
        });

        if (summarizedTextRef.current) {
            observer.observe(summarizedTextRef.current);
        }

        return () => observer.disconnect();
    }, [summarizedText]);



    const addNewParameter = (type) => {
        setEditableReport(prev => {
            if (!prev) return prev;

            const updated = { ...prev };
            const newParam = {
                name: '',
                value: '',
                status: 'normal',
                reference_range: '',
                reason: ''
            };

            updated[type] = [...(updated[type] || []), newParam];
            return updated;
        });
    };

    const removeParameter = (type, index) => {
        setEditableReport(prev => {
            if (!prev) return prev;

            const updated = { ...prev };
            const targetList = [...(updated[type] || [])];
            targetList.splice(index, 1);
            updated[type] = targetList;

            return updated;
        });
    };

    const renderMedia = () => {
        if (!selectedReport) return null;

        if (selectedReport.filetype === 'images') {
            return (
                <div className='Viewreportcard'>
                    <img src={selectedReportUrl} alt="Report Image" />
                </div>
            );
        } else if (selectedReport?.filetype?.includes('document') || selectedReport?.filetype?.includes('PDF')) {
            return (
                <div style={{ display: "flex", height: "100vh", width: "100%", padding: "1rem", overflow: "hidden", gap: "1rem", boxSizing: "border-box", }}>

                    <div style={{ flex: 1, p: 1, borderRadius: 0, display: "flex", flexDirection: "column", width: "100%" }}>
                        <Typography variant="h6" sx={{
                            mb: 1, textAlign: "center", fontFamily: "'Poppins', sans-serif",
                            fontWeight: 500,
                        }}>
                            PDF Preview
                        </Typography>
                        <div className='flex items-center justify-start mb-2'>
                            <Button
                                sx={{
                                    width: "15rem",
                                    mt: 2,
                                    backgroundColor: 'var(--themePrimaryColor, #DA6E0B)',
                                    color: 'white',
                                    borderRadius: '5px',
                                    padding: "10px 1rem",
                                    textTransform: 'none',
                                    fontFamily: "'Poppins', sans-serif",
                                    fontWeight: 500,
                                    '&:hover': {
                                        backgroundColor: '#B55A09',
                                        color: 'white'
                                    }
                                }}
                                onClick={() => handleDownload(selectedReportUrl)}
                            >
                                Download PDF
                            </Button>
                        </div>


                        <Box sx={{ flex: 1, overflow: "hidden" }}>
                            <iframe
                                src={`https://docs.google.com/gview?url=${selectedReportUrl}&embedded=true`}
                                width="100%"
                                height="100%"
                                title="PDF Document"
                                style={{ border: "none" }}
                            ></iframe>
                        </Box>
                    </div>

                    <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "auto", width: "100%" }}>
                        <div className="w-full mt-2">
                            <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "auto", width: "100%" }}>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        mb: 1,
                                        textAlign: "center",
                                        fontFamily: "'Poppins', sans-serif",
                                        fontWeight: 500,
                                    }}
                                >
                                    Report Summary
                                </Typography>

                                <div className="w-full mt-2">
                                    <div className="flex items-center justify-end gap-2 mb-2">
                                        <Button
                                            variant="contained"
                                            style={{ backgroundColor: "#4CAF50" }}
                                            onClick={uploadReportandExtractData}
                                            disabled={isExtracting}
                                            startIcon={isExtracting ? <CircularProgress size={20} color="inherit" /> : <ArrowUpward />}
                                        >
                                            {isExtracting ? 'Processing...' : 'Re-analyze'}
                                        </Button>
                                        <Button
                                            variant="contained"
                                            style={{ backgroundColor: "var(--themePrimaryColor, #DA6E0B)" }}
                                            onClick={editMode ? handleSave : toggleEdit}
                                        >
                                            {editMode ? 'Save' : 'Edit'}
                                        </Button>
                                    </div>

                                    {editableReport ? (
                                        editMode ? (
                                            <div>
                                                <TableContainer component={Paper}>
                                                    <Table>
                                                        <TableHead>
                                                            <TableRow>
                                                                <TableCell><strong>Name</strong></TableCell>
                                                                <TableCell><strong>Value</strong></TableCell>
                                                                <TableCell><strong>Status</strong></TableCell>
                                                                <TableCell><strong>Reference Range</strong></TableCell>
                                                                <TableCell><strong>Note</strong></TableCell>
                                                                <TableCell><strong>Actions</strong></TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {["abnormal_parameters", "normal_parameters"].flatMap(type =>
                                                                (editableReport[type] || []).map((param, j) => (
                                                                    <TableRow key={`${type}-${j}`}>
                                                                        <TableCell>
                                                                            <TextField
                                                                                size="medium"
                                                                                placeholder="Parameter name"
                                                                                value={param.name || ''}
                                                                                onChange={(e) =>
                                                                                    handleParamChange(type, j, "name", e.target.value)
                                                                                }
                                                                                sx={{ minWidth: '150px' }}
                                                                                fullWidth
                                                                            />
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            <TextField
                                                                                size="medium"
                                                                                placeholder="Value"
                                                                                value={param.value || ''}
                                                                                onChange={(e) =>
                                                                                    handleParamChange(type, j, "value", e.target.value)
                                                                                }
                                                                                sx={{ minWidth: '120px' }}
                                                                                fullWidth
                                                                            />
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            <FormControl size="small" fullWidth>
                                                                                <Select
                                                                                    value={param.status || ''}
                                                                                    onChange={(e) =>
                                                                                        handleParamChange(type, j, "status", e.target.value)
                                                                                    }
                                                                                >
                                                                                    <MenuItem value="normal">Normal</MenuItem>
                                                                                    <MenuItem value="low">Low</MenuItem>
                                                                                    <MenuItem value="high">High</MenuItem>
                                                                                    <MenuItem value="borderline">Borderline</MenuItem>
                                                                                    <MenuItem value="borderline_low">Borderline Low</MenuItem>
                                                                                    <MenuItem value="borderline_high">Borderline High</MenuItem>
                                                                                    <MenuItem value="critical">Critical</MenuItem>
                                                                                </Select>
                                                                            </FormControl>
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            <TextField
                                                                                size="medium"
                                                                                placeholder="Reference range"
                                                                                value={param.reference_range || ''}
                                                                                onChange={(e) =>
                                                                                    handleParamChange(type, j, "reference_range", e.target.value)
                                                                                }
                                                                                sx={{ minWidth: '140px' }}
                                                                                fullWidth
                                                                            />
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            <TextField
                                                                                size="medium"
                                                                                placeholder="Note/Reason"
                                                                                value={param.reason || ''}
                                                                                onChange={(e) =>
                                                                                    handleParamChange(type, j, "reason", e.target.value)
                                                                                }
                                                                                sx={{ minWidth: '160px' }}
                                                                                fullWidth
                                                                                multiline
                                                                                maxRows={3}
                                                                            />
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            <IconButton
                                                                                size="small"
                                                                                color="error"
                                                                                onClick={() => removeParameter(type, j)}
                                                                                title="Remove parameter"
                                                                            >
                                                                                <Delete fontSize="small" />
                                                                            </IconButton>
                                                                        </TableCell>
                                                                    </TableRow>
                                                                ))
                                                            )}
                                                        </TableBody>
                                                    </Table>
                                                </TableContainer>

                                                <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        startIcon={<Add />}
                                                        onClick={() => addNewParameter('abnormal_parameters')}
                                                        sx={{
                                                            borderColor: '#ff6b6b',
                                                            color: '#ff6b6b',
                                                            '&:hover': {
                                                                borderColor: '#ff5252',
                                                                backgroundColor: '#fff5f5'
                                                            }
                                                        }}
                                                    >
                                                        Add Abnormal Parameter
                                                    </Button>
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        startIcon={<Add />}
                                                        onClick={() => addNewParameter('normal_parameters')}
                                                        sx={{
                                                            borderColor: '#4caf50',
                                                            color: '#4caf50',
                                                            '&:hover': {
                                                                borderColor: '#45a049',
                                                                backgroundColor: '#f1f8e9'
                                                            }
                                                        }}
                                                    >
                                                        Add Normal Parameter
                                                    </Button>
                                                </Box>
                                            </div>
                                        ) : (
                                            ["abnormal_parameters", "normal_parameters"].flatMap(type =>
                                                (editableReport[type] || []).map((param, j) => {
                                                    const status = (param.status || "").toLowerCase();
                                                    const backgroundColor =
                                                        status === "low" || status === "high"
                                                            ? "#FFF5F5"
                                                            : status === "normal"
                                                                ? "var(--themeSecondaryColor, #F1EFEC)"
                                                                : "#F5F5F5";

                                                    return (
                                                        <Accordion key={`${type}-${j}`} sx={{ backgroundColor, color: "#7C7870", mb: 1 }}>
                                                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                                                <Box className="flex items-center gap-2 w-full" sx={{ fontFamily: "'Poppins', sans-serif" }}>
                                                                    {getIcon(status)}
                                                                    <Box className="flex items-center justify-between gap-2 w-full">
                                                                        <Box className="flex items-center gap-2">
                                                                            <span>{param.name}</span>
                                                                            <Typography variant="body2">({param.value})</Typography>
                                                                        </Box>
                                                                        {getStatusTag(status)}
                                                                    </Box>
                                                                </Box>
                                                            </AccordionSummary>
                                                            <AccordionDetails>
                                                                <Typography variant="body2" sx={{ color: "#333" }}>
                                                                    <strong>Reference Range:</strong> {param.reference_range}
                                                                </Typography>
                                                                {param.reason && (
                                                                    <Typography variant="body2" sx={{ color: "#555", mt: 1 }}>
                                                                        <strong>Note:</strong> {param.reason}
                                                                    </Typography>
                                                                )}
                                                            </AccordionDetails>
                                                        </Accordion>
                                                    );
                                                })
                                            )
                                        )
                                    ) : null}
                                </div>
                            </div>
                        </div>
                    </div>

                </div >
            );
        }
    };

    const handleSummarize = async () => {
        try {
            setSummarizeloading(true);
            const res = await reportsModel.post('/checkQuery', {
                query: `[${extractedData}] Summarise it`
            }, {
                headers: {
                    "Content-Type": "application/json",
                },
                withCredentials: false,
            });

            if (res.status === 200) {
                setsummarizedText(res.data);
                setSummarizeloading(false);
                if (summarizedTextRef.current) {
                    summarizedTextRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
                }
            }
        } catch (err) {
            console.error("Summarization Error:", err);
            setSummarizeloading(false);
        }
    };

    return (
        <div className="All-products-section-main w-full">
            <nav className="flex flex-row p-2 items-center justify-between">
                <div className="flex flex-row items-center">
                    <IconButton onClick={() => navigate(-1)}>
                        <ChevronLeft />
                    </IconButton>
                    <p className="mb-0">Reports</p>
                </div>
            </nav>


            <div className="Reports-Sections w-full flex flex-col gap-4 p-4">

                {renderMedia()}

                <div className="summarizedtext flex-1" ref={summarizedTextRef}>
                    {summarizedText && (
                        <>
                            <h2>Summarized Text</h2>
                            <div className="mt-4">
                                <p dangerouslySetInnerHTML={{
                                    __html: summarizedText
                                        ?.replace(/\n/g, '<br>')
                                        .replace(/\*\*(.*?)\*\*/g, '<h4>$1</h4>')
                                }}></p>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>

    );
}

export default ViewReport;
